import { useEffect } from "react";
import Navbar from "./components/Navbar";
import HeroSection from "./components/HeroSection";
import ProblemSection from "./components/ProblemSection";
import SolutionSection from "./components/SolutionSection";
import BenefitsSection from "./components/BenefitsSection";
import ProofSection from "./components/ProofSection";
import ReviewsSection from "./components/ReviewsSection";
import EmergencyBlock from "./components/EmergencyBlock";
import FaqSection from "./components/FaqSection";
import ContactSection from "./components/ContactSection";
import Footer from "./components/Footer";
import MobileContactBar from "./components/MobileContactBar";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${SITE_URL}/#business`,
  name: "IGM Facility Service",
  description:
    "Professioneller Facility Service in Ratingen und ganz NRW: Gebäudereinigung, Hausmeisterservice, Grünpflege, Fensterreinigung, Entrümpelung sowie Renovierung und Sanierung – alles aus einer Hand.",
  url: SITE_URL,
  telephone: "+492102739807",
  email: "info@igm-facility.de",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Ratingen",
    addressLocality: "Ratingen",
    addressRegion: "Nordrhein-Westfalen",
    postalCode: "40878",
    addressCountry: "DE",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 51.2977,
    longitude: 6.8502,
  },
  areaServed: [
    "Ratingen",
    "Düsseldorf",
    "Mettmann",
    "Erkrath",
    "Hilden",
    "Langenfeld",
    "Velbert",
    "Heiligenhaus",
    "Wülfrath",
    "Neuss",
    "Krefeld",
    "Nordrhein-Westfalen",
  ],
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "10:00",
      closes: "15:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Saturday"],
      opens: "12:00",
      closes: "15:00",
    },
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Facility Services NRW",
    itemListElement: [
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Gebäudereinigung Ratingen" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Hausmeisterservice NRW" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Grünpflege Ratingen" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Fensterreinigung NRW" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Entrümpelung Ratingen" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Renovierung und Sanierung NRW" } },
    ],
  },
  award: ["TrustLocal Top Pro 2022", "TrustLocal Top Score 2024", "TrustLocal Top Pro 2024"],
  numberOfEmployees: { "@type": "QuantitativeValue", minValue: 50 },
  sameAs: [
    "https://trustlocal.de/nordrhein-westfalen/ratingen/reinigungsfirma/amaterasu-facility/",
  ],
};

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: "IGM Facility Service",
  description: "Facility Service NRW – Gebäudereinigung, Hausmeister, Grünpflege & Sanierung aus einer Hand.",
  inLanguage: "de-DE",
  publisher: { "@id": `${SITE_URL}/#business` },
};

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${SITE_URL}/#webpage`,
  url: SITE_URL,
  name: "Ratingen Facility Service NRW – IGM Gebäudereinigung & Hausmeister",
  description:
    "IGM Facility Service in Ratingen: Professionelle Gebäudereinigung, Hausmeisterservice, Grünpflege & Sanierung in NRW. Kurzfristige Einsätze, 50+ Mitarbeiter, NRW-weit tätig.",
  inLanguage: "de-DE",
  isPartOf: { "@id": `${SITE_URL}/#website` },
  about: { "@id": `${SITE_URL}/#business` },
  dateModified: "2026-04-09",
  breadcrumb: {
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Startseite",
        item: SITE_URL,
      },
    ],
  },
};

export default function Home() {
  useEffect(() => {
    const injectSchema = (id: string, data: object) => {
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement("script");
        el.id = id;
        (el as HTMLScriptElement).type = "application/ld+json";
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(data);
    };

    injectSchema("schema-local-business", localBusinessSchema);
    injectSchema("schema-website", websiteSchema);
    injectSchema("schema-webpage", webPageSchema);

    return () => {
      ["schema-local-business", "schema-website", "schema-webpage"].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  return (
    <main className="min-h-screen bg-white pb-20 md:pb-0" style={{ fontFamily: "'Inter', 'DM Sans', sans-serif" }}>
      <Navbar />
      <HeroSection />
      <ProblemSection />
      <SolutionSection />
      <BenefitsSection />
      <ProofSection />
      <ReviewsSection />
      <EmergencyBlock />
      <FaqSection />
      <ContactSection />
      <Footer />
      <MobileContactBar />
    </main>
  );
}
